/**
 * Entry point for all mocks
 * This file exports all the mocked Node.js modules and functions
 */

import * as fs from './fs.js';
import * as path from './path.js';
import * as url from './url.js';
import * as crypto from './crypto.js';
import * as events from './events.js';
import * as http from './http.js';
import * as nodeDataChannel from './node-datachannel.js';
import * as ws from './ws.js';
import * as zeromq from './zeromq.js';
import jiti from './jiti.js';
import * as assert from './assert.js';
import * as zlib from './zlib.js';
import { createExpressApp, MockExpress, MockExpressRequest, MockExpressResponse } from './express.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
const isNodeRuntime = typeof process !== 'undefined' && !!process.versions?.node;
const globalAny: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;

const ensureGlobals = (): void => {
  if (!isBrowser || isNodeRuntime || !globalAny) return;
  if (globalAny.__GOTTI_NODE_MOCKS_READY__) return;

  if (!globalAny.Buffer) {
    try {
      const { Buffer } = require('buffer');
      globalAny.Buffer = Buffer;
    } catch {}
  }

  const existingProcess = globalAny.process ?? {};
  const stdout: any = existingProcess.stdout ?? {};
  const stderr: any = existingProcess.stderr ?? {};
  if (typeof stdout.write !== 'function') {
    stdout.write = () => true;
  }
  if (typeof stdout.fd !== 'number') {
    stdout.fd = 1;
  }
  if (typeof stderr.write !== 'function') {
    stderr.write = () => true;
  }
  if (typeof stderr.fd !== 'number') {
    stderr.fd = 2;
  }
  globalAny.process = {
    ...existingProcess,
    env: { ...(existingProcess.env ?? {}) },
    cwd: existingProcess.cwd ?? (() => './'),
    versions: existingProcess.versions ?? { node: '0.0.0-browser' },
    stdout,
    stderr,
  };
  const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const hrtime = (time?: [number, number]): [number, number] => {
    const ms = now();
    const seconds = Math.floor(ms / 1000);
    const nanoseconds = Math.floor((ms % 1000) * 1e6);
    if (!time) {
      return [seconds, nanoseconds];
    }
    let diffSeconds = seconds - time[0];
    let diffNanoseconds = nanoseconds - time[1];
    if (diffNanoseconds < 0) {
      diffSeconds -= 1;
      diffNanoseconds += 1_000_000_000;
    }
    return [diffSeconds, diffNanoseconds];
  };

  if (typeof globalAny.process.hrtime !== 'function') {
    globalAny.process.hrtime = hrtime;
    globalAny.process.hrtime.bigint = () => {
      const ms = now();
      return BigInt(Math.floor(ms * 1e6));
    };
  }

  if (typeof globalAny.setImmediate !== 'function') {
    globalAny.setImmediate = (fn: (...args: any[]) => void, ...args: any[]) => setTimeout(() => fn(...args), 0);
  }
  if (typeof globalAny.clearImmediate !== 'function') {
    globalAny.clearImmediate = (handle: number) => clearTimeout(handle);
  }

  globalAny.exports = globalAny.exports ?? {};
  globalAny.module = globalAny.module ?? { exports: globalAny.exports };
  globalAny.assert = globalAny.assert ?? assert;
  globalAny.zlib = globalAny.zlib ?? zlib;
  globalAny.require = globalAny.require ?? ((id: string) => {
    if (id === 'fs' || id === 'node:fs') return fs;
    if (id === 'path' || id === 'node:path') return path;
    if (id === 'url' || id === 'node:url') return url;
    if (id === 'crypto' || id === 'node:crypto') return crypto;
    if (id === 'events') return events;
    if (id === 'http' || id === 'https') return http;
    if (id === 'express') return createExpressApp;
  if (id === 'jiti') return jiti;
    if (id === 'zeromq') return zeromq;
    if (id === 'ws') return ws;
    if (id === 'node-datachannel') return nodeDataChannel;
  if (id === 'assert') return assert;
  if (id === 'zlib') return zlib;
    return {};
  });

  globalAny.__GOTTI_NODE_MOCKS_READY__ = true;
};

ensureGlobals();

// Export all mocks
export {
  fs,
  path,
  url,
  crypto,
  events,
  http,
  nodeDataChannel,
  ws,
  zeromq,
  jiti,
  assert,
  zlib,

  // Express mocks
  createExpressApp,
  MockExpress,
  MockExpressRequest,
  MockExpressResponse
};

// Setup function to initialize all mocks
export const setupMocks = (): void => {
  ensureGlobals();
  if (!isBrowser || isNodeRuntime) return;

  const oldRequire = globalAny.require;

  globalAny.require = (id: string): any => {
    if (id === 'fs' || id === 'node:fs') return fs;
    if (id === 'path' || id === 'node:path') return path;
    if (id === 'url' || id === 'node:url') return url;
    if (id === 'crypto' || id === 'node:crypto') return crypto;
    if (id === 'express') return createExpressApp;
    if (id === 'events') return events;
    if (id === 'http' || id === 'https') return http;
    if (id === 'zeromq') return zeromq;
    if (id === 'ws') return ws;
    if (id === 'node-datachannel') return nodeDataChannel;
  if (id === 'jiti') return jiti;
  if (id === 'assert') return assert;
  if (id === 'zlib') return zlib;
    return typeof oldRequire === 'function' ? oldRequire(id) : {};
  };
};

// Setup Node.js global polyfills
export const setupNodePolyfills = (): void => {
  ensureGlobals();
  if (!isBrowser || isNodeRuntime || !globalAny) return;

  globalAny.__dirname = globalAny.__dirname ?? './';
  globalAny.__filename = globalAny.__filename ?? './index.js';
  globalAny.setImmediate = globalAny.setImmediate ?? ((fn: Function, ...args: any[]) => setTimeout(() => fn(...args), 0));
};

// Re-export old named exports for compatibility
export { setupMocks as initializeMocks };
export { setupMocks as setupMockRequire };

export default setupMocks;
